'use client'

import { Text } from '@/components/ui/text'
import useResizeObserver from '@/hooks/useResizeObserver'
import { Box, Flex, styled } from '@/styled-system/jsx'
import { token } from '@/styled-system/tokens'
import { degToRad } from '@/utils/math'
import { animate, useAnimationFrame } from 'framer-motion'
import { memo, useEffect, useRef, useState } from 'react'
import { WHEEL_PAYOUT_COLORS } from './constants'

export type WheelGameWheelProps = {
  payouts: number[]
  result?: number | null
  disableAnimation?: boolean
  onAnimationEnd?: () => void
}

const WheelGameWheel = memo(function WheelGameWheel({
  payouts,
  result,
  disableAnimation,
  onAnimationEnd,
}: WheelGameWheelProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rotationRef = useRef(0)
  const payoutsRef = useRef(payouts)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    payoutsRef.current = payouts
  }, [payouts])

  useEffect(() => {
    if (result == null) {
      return
    }

    if (disableAnimation) {
      rotationRef.current = degToRad(360 - (360 / payouts.length) * result)
      onAnimationEnd?.()
      return
    }

    setIsAnimating(true)

    animate(
      rotationRef.current,
      degToRad(
        1080 - (360 / payouts.length) * (result - 0.25 + 0.5 * Math.random())
      ),
      {
        duration: 1,
        ease: 'easeOut',
        onUpdate: (latest: number) => {
          rotationRef.current = latest % (2 * Math.PI)
        },
      }
    ).then(() => {
      setIsAnimating(false)
      onAnimationEnd?.()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result])

  useResizeObserver(containerRef, (entries) => {
    const { width } = entries[0].contentRect
    const target = entries[0].target as HTMLElement
    target.style.fontSize = `${width / 550}em`
  })

  useAnimationFrame(() => {
    const canvas = canvasRef.current

    if (!canvas) {
      return
    }

    canvas.width = canvas.clientWidth
    canvas.height = canvas.clientHeight

    const ctx = canvas.getContext('2d')

    if (!ctx) {
      return
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    ctx.save()
    ctx.scale(canvas.width / 550, canvas.height / 550)

    ctx.save()
    ctx.translate(275, 275)
    ctx.rotate(rotationRef.current)

    ctx.fillStyle = token('colors.neutral.200')
    ctx.beginPath()
    ctx.arc(0, 0, 245 * 1.05, 0, 2 * Math.PI)
    ctx.fill()
    ctx.closePath()

    const segmentColors = payoutsRef.current
    const totalSegments = segmentColors.length
    const arc = degToRad(360 / totalSegments)

    segmentColors.forEach((payout, index) => {
      ctx.save()

      ctx.rotate(arc * index - Math.PI / 2)

      ctx.fillStyle = WHEEL_PAYOUT_COLORS[payout]
      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.arc(0, 0, 245 * 0.965, -arc / 2, arc / 2)
      ctx.lineTo(0, 0)
      ctx.fill()

      ctx.restore()
    })

    ctx.strokeStyle = 'rgba(9, 24, 54, 0.08)'
    ctx.lineWidth = 245 * 0.021
    ctx.beginPath()
    ctx.arc(0, 0, 245 - 245 * 0.0455, 0, 2 * Math.PI)
    ctx.stroke()

    ctx.fillStyle = token('colors.neutral.100')
    ctx.beginPath()
    ctx.arc(0, 0, 245 * 0.88, 0, 2 * Math.PI)
    ctx.fill()

    ctx.restore()

    // Arrow
    const width = 23.75
    const r = width / 2
    ctx.save()
    ctx.translate(275, 12)
    ctx.fillStyle = '#E90083'
    ctx.beginPath()
    ctx.arc(0, 0, r, 0, 2 * Math.PI)
    ctx.fill()
    ctx.beginPath()
    ctx.moveTo(-r - 0.05, 0.5)
    ctx.lineTo(r - 0.05, 0.5)
    ctx.lineTo(0, 45.125)
    ctx.fill()
    ctx.fillStyle = '#3A2842'
    ctx.beginPath()
    ctx.arc(0, 0, width / 3, 0, 2 * Math.PI)
    ctx.fill()
    ctx.restore()

    ctx.restore()
  })

  return (
    <Box
      ref={containerRef}
      position='relative'
      w='full'
      maxW='550px'
      _after={{
        content: '""',
        display: 'block',
        pb: '100%',
      }}
    >
      <styled.canvas
        ref={canvasRef}
        position='absolute'
        top={0}
        left={0}
        right={0}
        bottom={0}
        w='full'
        h='full'
      />

      <Flex
        position='absolute'
        top='50%'
        left='50%'
        transform='translate(-50%, -50%) scale(1)'
        align='center'
        justify='center'
        w='10em'
        h='10em'
        borderWidth='0.125em'
        borderColor='currentColor'
        borderRadius='full'
        color='neutral.300'
        style={{
          color:
            !isAnimating && result != null
              ? WHEEL_PAYOUT_COLORS[payouts[result]]
              : undefined,
        }}
      >
        {!isAnimating && result != null && (
          <Box
            position='absolute'
            top='50%'
            left='50%'
            transform='translate(-50%, -50%) scale(1)'
            animationName='hilo-guess'
            animationDuration='0.3s'
            animationTimingFunction='cubic-bezier(0.87, 0, 0.13, 1)'
          >
            <Text fontSize='2.5em' fontWeight='semibold'>
              {payouts[result].toFixed(2)}×
            </Text>
          </Box>
        )}
      </Flex>
    </Box>
  )
})

export default WheelGameWheel
